import React, { createContext, useContext, useState, useEffect } from "react";
import clsx from "clsx";
import BottomSheetModal from "../components/BottomSheetModal";
import { ReactComponent as NoInternetIcon } from '../assets/no-internet-icon.svg';
import connectingIcon from "../assets/wifi-theme-icon.svg";
import styles from "./styles.module.css";
import ImageComponent from "../components/ImageComponent";

// Network status constants
const NETWORK_STATUS = {
  CHECKING: "Checking...",
  NO_NETWORK: "No Network",
  GOOD: "Good",
  WEAK: "Weak",
  VERY_WEAK: "Very Weak"
};

// Event type constants
const EVENT_TYPES = {
  ONLINE: "online",
  OFFLINE: "offline"
};

const NetworkContext = createContext<{ status: string }>({ status: navigator.onLine ? NETWORK_STATUS.CHECKING : NETWORK_STATUS.NO_NETWORK });

const NetworkProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<string>(navigator.onLine ? NETWORK_STATUS.CHECKING : NETWORK_STATUS.NO_NETWORK);
  const [showModal, setShowModal] = useState<boolean>(!navigator.onLine);

  useEffect(() => {
    const updateNetworkStatus = () => {
      if (!navigator.onLine) {
        setStatus(NETWORK_STATUS.NO_NETWORK);
        setShowModal(true);
      } else {
        const connection =
          (navigator as any).connection ||
          (navigator as any).mozConnection ||
          (navigator as any).webkitConnection;

        if (connection && connection.downlink) {
          if (connection.downlink > 0.5) {
            setStatus(NETWORK_STATUS.GOOD);
            setShowModal(false);
          } else if (connection.downlink > 0.25) {
            setStatus(NETWORK_STATUS.WEAK);
            setShowModal(true);
          } else {
            setStatus(NETWORK_STATUS.VERY_WEAK);
            setShowModal(true);
          }
        } else {
          setStatus(NETWORK_STATUS.GOOD);
          setShowModal(false);
        }
      }
    };

    window.addEventListener(EVENT_TYPES.ONLINE, updateNetworkStatus);
    window.addEventListener(EVENT_TYPES.OFFLINE, updateNetworkStatus);

    updateNetworkStatus();

    return () => {
      window.removeEventListener(EVENT_TYPES.ONLINE, updateNetworkStatus);
      window.removeEventListener(EVENT_TYPES.OFFLINE, updateNetworkStatus);
    };
  }, []);

  return (
    <NetworkContext.Provider value={{ status }}>
      {children}
      {showModal && (
        <BottomSheetModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          modalContentClassName={clsx(styles.modalContent)}
          content={status === NETWORK_STATUS.NO_NETWORK ?
            <div className={clsx(styles.noNetworkContainer)}>
              <div className={clsx(styles.iconBox)}>
                <NoInternetIcon />
              </div>
              <div className={clsx(styles.contentBox)}>
                <div className={clsx(styles.heading, "fontSizeSm18 fontWeightBold fontColorBlack")}>No Internet</div>
                <div className={clsx(styles.description, "fontWeightRegular fontSizeSm14 fontColor666")}>Move to an area with better network to be back in the game</div>
              </div>
            </div>
            :
            <div className={clsx(styles.reconnectingContainer, "flex alignItemCenter justifyContentFS textAlignLeft")}>
              <div className={clsx(styles.iconBox)}>
                <ImageComponent
                  src={connectingIcon}
                  alt="connecting icon"
                  className={clsx(styles.icon)}
                />
              </div>
              <div className={clsx(styles.contentBox)}>
                <div className={clsx(styles.heading, "fontSizeSm14 fontWeightBold fontColorBlack")}>Reconnecting</div>
                <div className={clsx(styles.description, "fontWeightRegular fontSizeSm12 fontColor666")}>Move to an area with better network to avoid missing your turn</div>
              </div>
            </div>
          }
        />
      )}
    </NetworkContext.Provider>
  );
};

// Hook to use network status
export const useNetworkStatus = () => {
  return useContext(NetworkContext);
};

export default NetworkProvider;
